import { useCallback, useContext } from 'react'
import { getPageUrl, PageType } from './routes'
import { SiteContext } from './site-context'
import { PageHeroContext } from './page-hero-context'
import { LanguageContext } from './language'
import { PageModuleContext } from './page-module-context'
import { SeoContext } from './seo-context'
import { LayoutContext } from './layout-context'
import { useRouter } from 'next/router'
import { getSanityClient } from './sanity/client'
import { getCasePage } from './sanity/page'
import {
  SanityColor,
  SanityHeroModule,
} from '@data/sanity/queries/types/modules'
import { getSanityImageUrl } from './image'

export const linkFrameColorClasses: Record<SanityColor, string> = {
  [SanityColor.WHITE]: 'text-white',
  [SanityColor.GRAY]: 'text-gray',
  [SanityColor.GREEN_LIGHT]: 'text-green-light',
  [SanityColor.GREEN]: 'text-green',
  [SanityColor.GREEN_ELECTRIC]: 'text-green-electric',
  [SanityColor.GREEN_DARK]: 'text-green-dark',
  [SanityColor.PINK_FADED]: 'text-pink-faded',
  [SanityColor.PINK]: 'text-pink',
  [SanityColor.PINK_HOT]: 'text-pink-hot',
  [SanityColor.BLUE]: 'text-blue',
  [SanityColor.RED]: 'text-red',
  [SanityColor.YELLOW]: 'text-yellow',
}

/**
 * Hook that returns a callback which opens case page without Next.js navigation.
 */
export const useOpenCasePage = () => {
  const { togglePageTransition } = useContext(SiteContext)
  const { locale } = useContext(LanguageContext)
  const { setCasePageModules } = useContext(PageModuleContext)
  const {
    setSourcePage,
    setTargetPage,
    setHeroCaseVideoCarouselDisabled,
    setCaseHeroModule,
  } = useContext(PageHeroContext)
  const {
    setSiteTitle,
    setMetaTitle,
    setMetaDescription,
    setShareTitle,
    setShareDescription,
    setShareGraphicUrl,
  } = useContext(SeoContext)
  const { setIsHeaderTransparent, setHeaderBackgroundColor } =
    useContext(LayoutContext)

  const router = useRouter()

  return useCallback(
    async (caseSlug: string) => {
      // Start route change progress bar
      togglePageTransition(true)

      // Disable case video carousel (continue the current video)
      setHeroCaseVideoCarouselDisabled(true)

      // Get case page data
      const sanityClient = getSanityClient()
      const casePageData = await getCasePage(sanityClient, locale, caseSlug)

      // Update hero module
      const newHeroModule = (casePageData.page?.modules ?? []).filter(
        (module, index) => {
          return index === 0 && module._type === 'hero'
        },
      )[0] as SanityHeroModule | undefined
      setCaseHeroModule(newHeroModule)

      // Replace page modules
      const newCasePageModules = (casePageData.page?.modules ?? []).filter(
        (module, index) => {
          return index > 0 || module._type !== 'hero'
        },
      )
      setCasePageModules(newCasePageModules)

      // Update page title and SEO metadata
      setSiteTitle(casePageData.site.seo?.siteTitle)
      setMetaTitle(
        casePageData?.page?.seo?.metaTitle ?? casePageData.site.seo?.metaTitle,
      )
      setMetaDescription(
        casePageData?.page?.seo?.metaDesc ?? casePageData.site.seo?.metaDesc,
      )
      setShareTitle(
        casePageData?.page?.seo?.shareTitle ??
          casePageData.site.seo?.shareTitle,
      )
      setShareDescription(
        casePageData?.page?.seo?.shareDesc ?? casePageData.site.seo?.shareDesc,
      )
      setShareGraphicUrl(
        getSanityImageUrl(
          casePageData?.page?.seo?.shareGraphic ??
            casePageData.site.seo?.shareGraphic,
          {
            width: 1200,
            height: 630,
          },
        ),
      )

      // Update header color and transparency
      setHeaderBackgroundColor(casePageData.page?.headerBackgroundColor)
      setIsHeaderTransparent(!!casePageData.page?.hasTransparentHeader)

      // Update page URL using window history not Next.js router (see also _app.tsx handlePopState)
      const casePageTitle =
        casePageData?.page?.seo?.metaTitle ?? casePageData.site.seo?.metaTitle
      const casePageUrl = getPageUrl(PageType.CASE, caseSlug)
      window.history.pushState({}, casePageTitle ?? '', casePageUrl)

      // Finish route change progress bar
      togglePageTransition(false)

      // Save URLs to fix issues with navigation not working
      setSourcePage(router.asPath)
      setTargetPage(casePageUrl)
    },
    [
      locale,
      router,
      setCaseHeroModule,
      setCasePageModules,
      setHeaderBackgroundColor,
      setHeroCaseVideoCarouselDisabled,
      setIsHeaderTransparent,
      setMetaDescription,
      setMetaTitle,
      setShareDescription,
      setShareGraphicUrl,
      setShareTitle,
      setSiteTitle,
      setSourcePage,
      setTargetPage,
      togglePageTransition,
    ],
  )
}
